export type DiffLineKind = 'context' | 'add' | 'del';

export interface DiffLine {
  kind: DiffLineKind;
  content: string;
  oldNumber: number | null;
  newNumber: number | null;
}

export interface DiffHunk {
  header: string;
  lines: DiffLine[];
}

export interface ParsedDiff {
  hunks: DiffHunk[];
  additions: number;
  deletions: number;
}

const HUNK_HEADER = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

/**
 * Parse a raw unified diff into hunks. Everything before the first `@@` (the
 * `diff --git`/`index`/`---`/`+++` preamble) is dropped — the file identity is
 * already known from the branch-diff listing.
 */
export function parseDiff(raw: string): ParsedDiff {
  const hunks: DiffHunk[] = [];
  let current: DiffHunk | null = null;
  let oldNumber = 0;
  let newNumber = 0;
  let additions = 0;
  let deletions = 0;

  for (const line of raw.split('\n')) {
    const match = HUNK_HEADER.exec(line);
    if (match) {
      current = { header: line, lines: [] };
      hunks.push(current);
      oldNumber = Number(match[1]);
      newNumber = Number(match[2]);
      continue;
    }
    if (!current) continue;
    // "\ No newline at end of file" annotates the previous line, not a line of its own.
    if (line.startsWith('\\')) continue;

    if (line.startsWith('+')) {
      current.lines.push({ kind: 'add', content: line.slice(1), oldNumber: null, newNumber });
      newNumber += 1;
      additions += 1;
    } else if (line.startsWith('-')) {
      current.lines.push({ kind: 'del', content: line.slice(1), oldNumber, newNumber: null });
      oldNumber += 1;
      deletions += 1;
    } else {
      current.lines.push({
        kind: 'context',
        content: line.startsWith(' ') ? line.slice(1) : line,
        oldNumber,
        newNumber,
      });
      oldNumber += 1;
      newNumber += 1;
    }
  }

  // A trailing empty line is an artefact of splitting on '\n', not diff content.
  const last = hunks.at(-1);
  if (last && last.lines.at(-1)?.kind === 'context' && last.lines.at(-1)?.content === '') last.lines.pop();

  return { hunks, additions, deletions };
}

/**
 * "Hide whitespace" on the client: demote any added/removed line whose content is only whitespace
 * to a context line, so a diff that only re-indents or adds blank lines reads as unchanged.
 *
 * This is the pragmatic, in-browser version of `git diff -w` — it doesn't re-diff ignoring
 * intra-line whitespace, but it collapses the whitespace-only churn that dominates in practice.
 */
export function collapseWhitespace(hunks: DiffHunk[]): DiffHunk[] {
  return hunks.map((hunk) => ({
    header: hunk.header,
    lines: hunk.lines.map((line) =>
      line.kind !== 'context' && line.content.trim() === '' ? { ...line, kind: 'context' as const } : line,
    ),
  }));
}

export type SplitCellKind = DiffLineKind | 'empty';

export interface SplitCell {
  kind: SplitCellKind;
  content: string;
  number: number | null;
}

export interface SplitRow {
  left: SplitCell;
  right: SplitCell;
}

const EMPTY_CELL: SplitCell = { kind: 'empty', content: '', number: null };

/**
 * Align a hunk into left/right rows for a side-by-side view.
 *
 * Consecutive deletions and additions are paired positionally (line 1 of the removed block sits
 * opposite line 1 of the added block, etc.); the shorter side gets blank filler cells for the
 * overhang. Context lines flush any pending block and then occupy both columns.
 */
export function alignHunk(hunk: DiffHunk): SplitRow[] {
  const rows: SplitRow[] = [];
  let dels: DiffLine[] = [];
  let adds: DiffLine[] = [];

  const flush = () => {
    const count = Math.max(dels.length, adds.length);
    for (let i = 0; i < count; i += 1) {
      const del = dels[i];
      const add = adds[i];
      rows.push({
        left: del ? { kind: 'del', content: del.content, number: del.oldNumber } : EMPTY_CELL,
        right: add ? { kind: 'add', content: add.content, number: add.newNumber } : EMPTY_CELL,
      });
    }
    dels = [];
    adds = [];
  };

  for (const line of hunk.lines) {
    if (line.kind === 'del') {
      dels.push(line);
    } else if (line.kind === 'add') {
      adds.push(line);
    } else {
      flush();
      rows.push({
        left: { kind: 'context', content: line.content, number: line.oldNumber },
        right: { kind: 'context', content: line.content, number: line.newNumber },
      });
    }
  }
  flush();
  return rows;
}
